import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronRight, User } from 'lucide-react';

// 🔗 URL da área do aluno — troque aqui quando tiver o link oficial
const STUDENT_AREA_URL = 'https://wing-academy-brasil.memberkit.com.br/';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line
  }, []);

  // Fechar menu ao redimensionar para desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line
  }, []);

  const navLinks = [
    { name: 'Início', href: '#hero' },
    { name: 'Sobre', href: '#sobre' },
    { name: 'Módulos', href: '#modulos' },
    { name: 'Bônus', href: '#bonus' },
    { name: 'Depoimentos', href: '#depoimentos' },
    { name: 'FAQ', href: '#faq' },
  ];

  const specialLinks = [
    { name: '🎯 Fazer Quiz', href: '/quiz', highlight: true },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    
    if (href.startsWith('/')) {
      window.location.href = href;
      return;
    }
    
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'glass-dark py-2 md:py-3' : 'bg-transparent py-3 md:py-5'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <a href="#hero" onClick={(e) => scrollToSection(e, '#hero')} className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_behringer-school/artifacts/ej9d0d4x_LOGO%20WING%20ACADEMY%20BRANCA.png" 
                alt="WING ACADEMY BRASIL" 
                className="h-10 md:h-14 lg:h-16 w-auto"
              />
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
              {navLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="text-white hover:text-yellow-400 transition-colors duration-300 text-sm font-medium"
                >
                  {link.name}
                </a>
              ))}
              {specialLinks.map((link, index) => (
                <a
                  key={`special-${index}`}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className={`transition-colors duration-300 text-sm font-medium ${
                    link.highlight 
                      ? 'text-yellow-400 hover:text-yellow-300' 
                      : 'text-white hover:text-yellow-400'
                  }`}
                >
                  {link.name}
                </a>
              ))}
            </nav>

            {/* CTA Buttons Desktop */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href={STUDENT_AREA_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="student-area-btn-desktop"
                className="flex items-center gap-2 border border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10 hover:border-yellow-500 px-4 py-2.5 rounded-lg font-bold text-sm transition-all duration-300"
              >
                <User className="w-4 h-4" />
                Área do Aluno
              </a>
              <a
                href="#inscricao"
                onClick={(e) => scrollToSection(e, '#inscricao')}
                className="bg-green-500 hover:bg-green-600 text-white px-5 py-2.5 rounded-lg font-bold text-sm transition-all duration-300 hover:scale-105 glow-green"
              >
                INSCREVA-SE
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-white p-2 -mr-2"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/80 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Slide */}
      <div className={`fixed top-0 right-0 h-full w-[280px] bg-gray-900 z-50 transform transition-transform duration-300 lg:hidden ${
        isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="p-4">
          {/* Close button */}
          <div className="flex justify-end mb-6">
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-white p-2"
              aria-label="Fechar menu"
            >
              <X size={28} />
            </button>
          </div>

          {/* Navigation Links */}
          <nav className="flex flex-col space-y-1">
            {navLinks.map((link, index) => (
              <a
                key={index}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="flex items-center justify-between text-white hover:text-yellow-400 hover:bg-white/5 transition-all duration-300 py-3 px-3 rounded-lg text-base font-medium"
              >
                {link.name}
                <ChevronRight className="w-4 h-4 opacity-50" />
              </a>
            ))}
            
            {/* Divider */}
            <div className="border-t border-white/10 my-3"></div>
            
            {/* Special Links */}
            {specialLinks.map((link, index) => (
              <a
                key={`special-${index}`}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className={`flex items-center justify-between transition-all duration-300 py-3 px-3 rounded-lg text-base font-medium ${
                  link.highlight 
                    ? 'text-yellow-400 bg-yellow-500/10 hover:bg-yellow-500/20' 
                    : 'text-white hover:text-yellow-400 hover:bg-white/5'
                }`}
              >
                {link.name}
                <ChevronRight className="w-4 h-4 opacity-50" />
              </a>
            ))}

            {/* CTA Buttons (mobile menu) */}
            <div className="pt-4 space-y-3">
              <a
                href={STUDENT_AREA_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="student-area-btn-mobile"
                className="flex items-center justify-center gap-2 border-2 border-yellow-500 text-yellow-400 hover:bg-yellow-500/10 px-6 py-3.5 rounded-xl font-bold text-base transition-all duration-300"
              >
                <User className="w-5 h-5" />
                Área do Aluno
              </a>
              <a
                href="#inscricao"
                onClick={(e) => scrollToSection(e, '#inscricao')}
                className="block bg-green-500 hover:bg-green-600 text-white px-6 py-4 rounded-xl font-bold text-center transition-all duration-300 text-lg"
              >
                INSCREVA-SE AGORA
              </a>
            </div>
          </nav>
        </div>
      </div>
    </>
  );
};

export default Header;
